import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { addFavorite, getNavItems, deleteFavorite, updateFavorites, getNavFavorites } from './state/navigation.actions';
import {
  INavigationItem,
  INavigationFavorite,
  INavigationState,
  INavigationHeaderItem,
  INavigationChildItem
} from './state/navigation.model';
import { selectNavigationFavorites, selectNavItems, selectShouldHideNavigation } from './state/navigation.selectors';
import { LaunchDarklyService } from '../launch-darkly/launch-darkly.service';
import { ELaunchDarklyFlag } from '../../constants/pol.enums';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit, OnDestroy {
  launchDarkly = inject(LaunchDarklyService);
  navItems: INavigationItem[];
  favorites: INavigationFavorite[];
  favoritesUrls: string[];
  flatNavItems: Array<INavigationHeaderItem | INavigationChildItem>;
  currentRoute: string;
  isLoading: boolean = true;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  navItems$: Observable<INavigationItem[]> = this.store.select(selectNavItems).pipe(takeUntil(this.ngUnsubscribe));
  favorites$: Observable<INavigationFavorite[]> = this.store
    .select(selectNavigationFavorites)
    .pipe(takeUntil(this.ngUnsubscribe));
  shouldHide$ = this.store.select(selectShouldHideNavigation).pipe(takeUntil(this.ngUnsubscribe));

  protected clinicalFlag: boolean = false;
  protected pendingUsersFlag: boolean = false;
  protected umTeamFlag: boolean = false;
  protected viewAuth: boolean = false;
  protected claimsBillingFlag: boolean = false;

  private get navLdMap() {
    return {
      '62825af9-fa0f-4e5e-959f-03985674db4e': this.clinicalFlag,
      '1e14ca3c-1a50-471f-9c1a-9ba85b46398a': this.pendingUsersFlag,
      'fa298e6b-f477-451f-8795-46c01dffb622': this.umTeamFlag,
      'a95c037c-1888-47da-af4f-fd13ab7a2aff': this.viewAuth,
      '1a9a5e28-78f9-4f01-86e4-41cf762dc694': this.claimsBillingFlag
    };
  };

  skeletonLoaderData = [
    {
      ul: 'nav-skeleton-loading',
      li: [
        {
          elements: [
            {
              class: 'is--skeleton'
            }
          ]
        },
        {
          elements: [
            {
              class: 'is--skeleton is--skeleton--subitem'
            }
          ]
        },
        {
          elements: [
            {
              class: 'is--skeleton is--skeleton--subitem'
            }
          ]
        },
        {
          elements: [
            {
              class: 'is--skeleton'
            }
          ]
        },
        {
          elements: [
            {
              class: 'is--skeleton is--skeleton--subitem'
            }
          ]
        }
      ]
    }
  ];
  constructor(private store: Store<INavigationState>, private router: Router) { }

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged()
      )
      .subscribe((routerEvent: NavigationEnd) => {
        this.currentRoute = routerEvent.url;
      });
    this.isLoading = true;
    this.store.dispatch(getNavItems());
    this.store.dispatch(getNavFavorites());

    this.navItems$.subscribe((state: INavigationItem[]) => {
      this.isLoading = state === undefined ? true : false;
      if (state?.length > 0) {
        this.navItems = state;
        this.flatNavItems = this.flatNavItems ? this.flatNavItems : this.flattenNavItems(this.navItems);
        this.favorites$.subscribe((state: INavigationFavorite[]) => {
          this.favorites = this.populateFavoritesData(state, this.flatNavItems);
          this.favoritesUrls = this.favorites.map((item) => item?.siteUrl || item?.menuUrl);
        });
      }
    });

    // initial value
    this.launchDarkly.flags$.subscribe((res) => {
      this.clinicalFlag = res[ELaunchDarklyFlag.ClinicalModule];
      this.pendingUsersFlag = res[ELaunchDarklyFlag.SignUp];
      this.umTeamFlag = res[ELaunchDarklyFlag.ShowUMTeamAvailable];
      this.viewAuth = res[ELaunchDarklyFlag.ShowViewAuthorization];
      this.claimsBillingFlag = res[ELaunchDarklyFlag.ClaimsBillingModule]
    });

    // real-time updates
    [
      ELaunchDarklyFlag.ClinicalModule,
      ELaunchDarklyFlag.SignUp,
      ELaunchDarklyFlag.ShowUMTeamAvailable,
      ELaunchDarklyFlag.ShowViewAuthorization,
      ELaunchDarklyFlag.ClaimsBillingModule,
    ].forEach(i => {
      this.launchDarkly.watchForChanges<boolean>(i);
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  flattenNavItems(navItems: INavigationItem[]): Array<INavigationHeaderItem | INavigationChildItem> {
    const flattenItems = [];
    navItems.forEach((navItem: INavigationItem) => {
      flattenItems[navItem.menuData.menuGuid] = { ...navItem.menuData, navigationId: navItem.menuData.menuGuid };
      navItem.menuChildren.forEach(
        (child: INavigationChildItem) => (flattenItems[child.childGuid] = { ...child, navigationId: child.childGuid })
      );
    });
    return flattenItems;
  }

  populateFavoritesData(
    favorites: INavigationFavorite[],
    flatItems: Array<INavigationHeaderItem | INavigationChildItem>
  ): INavigationFavorite[] {
    // Only populate favorites data for navigationIds that exist in the current navigation
    return favorites?.reduce((result, fav) => {
      if (flatItems[fav.navigationId]) {
        return result.concat({
          ...flatItems[fav.navigationId]
        });
      }
      return result;
    }, []);
  }

  addToFavorites(favorite: INavigationFavorite): void {
    const navigationId = favorite?.menuGuid ? favorite.menuGuid : favorite.childGuid;
    if (!this.favorites.find((fav) => fav.navigationId === navigationId)) {
      const clonedFavorite: INavigationFavorite = { ...favorite, navigationId };
      this.store.dispatch(addFavorite({ favorite: clonedFavorite }));
    }
  }

  removeFromFavorites(favorite: INavigationFavorite): void {
    if (this.favorites.includes(favorite)) {
      this.store.dispatch(deleteFavorite({ favorite }));
    }
  }

  handleDrop(event: CdkDragDrop<string[]>): void {
    const newFavorites = this.moveItemInArray(this.favorites, event.previousIndex, event.currentIndex);
    this.store.dispatch(updateFavorites({ oldFavorites: this.favorites, newFavorites }));
  }

  moveItemInArray(array: INavigationFavorite[], fromIndex: number, toIndex: number): INavigationFavorite[] {
    const from = this.clamp$1(fromIndex, array.length - 1);
    const to = this.clamp$1(toIndex, array.length - 1);
    if (from === to) {
      return array;
    }
    const newArray = [...array];
    const target = newArray[from];
    const delta = to < from ? -1 : 1;
    for (let i = from; i !== to; i += delta) {
      newArray[i] = newArray[i + delta];
    }
    newArray[to] = target;
    return newArray;
  }

  clamp$1(value: number, max: number): number {
    return Math.max(0, Math.min(max, value));
  }

  isNavItemActive(navURl: string, isFavorite: boolean): boolean {
    const routeUrl = new URL(this.currentRoute, location.origin);
    const isCurrent = navURl === routeUrl.pathname;
    if (isFavorite) {
      return isCurrent;
    } else {
      return !this.favoritesUrls.includes(navURl) && isCurrent;
    }
  }

  isExternalLink(link: string): boolean {
    // check for http:// or https:// at the beginning of the link
    return link && link.match(/^http(s)?:\/\//) !== null;
  }

  checkLdFlags(guid: string): boolean {
    return this.navLdMap[guid] ?? true;
  }

  parseUrl(route: string): string {
    return route.split('?')[0];
  }

  parseQueryParams(route: string): { [key: string]: string } {
    const params = {};
    const paramStr = route.split('?')[1];

    if (paramStr) {
      for (const item of paramStr.split('&')) {
        const key = item.split('=')[0];
        const value = item.split('=')[1];
        params[key] = value;
      }
    }

    return params;
  }
}
